import { useRef, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { localeTagFromLang } from '../../utils/bookingLocaleDisplay';
import { BookingStepProgress } from './BookingStepProgress';
import './BookingComponents.css';
import './booking-brand.css';

export interface DateOption {
  date: string; // YYYY-MM-DD
  formatted: string;
  available: boolean;
  lowAvailability?: boolean;
}

export interface TimeSlot {
  timeString: string;
  slots: { id: string; startTime: string; available: boolean; available_capacity?: number }[];
  totalCapacity: number;
  representativeSlotId: string;
  /** false = el horario existe en la agenda pero está completo (se pinta tachado). */
  available?: boolean;
}

export interface ServiceSummary {
  name: string;
  duration?: string | number;
  price?: string | number;
  currency?: string;
}

export interface ResourceOption {
  id: string;
  name: string;
}

interface DateSlotSelectionProps {
  dates: DateOption[];
  selectedDate: string | null;
  timeSlots: TimeSlot[] | null;
  loadingSlots: boolean;
  onDateSelect: (date: string) => void;
  onSlotSelect: (slotId: string, timeString?: string) => void;
  language?: string;
  service?: ServiceSummary | null;
  requestedPartySize?: number;
  noSlotsMessage?: string;
  /** Profesionales elegibles. Con menos de 2 el backend no los envía y no se muestran chips. */
  resourceOptions?: ResourceOption[];
  selectedResourceId?: string | null;
  onResourceFilter?: (resourceId: string | null) => void;
}

function SlotSkeleton() {
  return (
    <div className="bk-slot-grid bk-slot-grid--skeleton">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="bk-slot-skeleton" />
      ))}
    </div>
  );
}

function parseIsoDateLocal(d: string): Date {
  const [y, m, day] = d.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, day || 1);
}

function weekdayShort(locale: string, d: Date): string {
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d).replace('.', '');
  } catch {
    return new Intl.DateTimeFormat('en', { weekday: 'short' }).format(d);
  }
}

function formatDuration(duration: string | number): string {
  if (typeof duration === 'number') return `${duration} min`;
  const parts = duration.split(':');
  if (parts.length === 3) {
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const total = hours * 60 + minutes;
    if (total >= 60 && total % 60 === 0) return `${total / 60}h`;
    if (total >= 60) return `${Math.floor(total / 60)}h ${total % 60}min`;
    return `${total} min`;
  }
  return String(duration);
}

function formatPrice(price: string | number, currency?: string): string {
  const num = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(num) || num === 0) return '';
  const curr = (currency || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${num} ${curr}`;
  }
}

/** Minutos desde medianoche de un "HH:MM"; -1 si no es parseable. */
function minutesOfDay(timeString: string): number {
  const m = /^(\d{1,2}):(\d{2})/.exec(timeString.trim());
  if (!m) return -1;
  return Number(m[1]) * 60 + Number(m[2]);
}

export function DateSlotSelection({
  dates,
  selectedDate,
  timeSlots,
  loadingSlots,
  onDateSelect,
  onSlotSelect,
  language = 'es',
  service,
  requestedPartySize = 1,
  noSlotsMessage,
  resourceOptions,
  selectedResourceId = null,
  onResourceFilter,
}: DateSlotSelectionProps) {
  const { t } = useTranslation();
  const tl = (key: string, opts?: Record<string, unknown>) =>
    t(key, { lng: language, ...(opts || {}) });
  const localeTag = localeTagFromLang(language);

  const stripWrapRef = useRef<HTMLDivElement>(null);
  const selectedBtnRef = useRef<HTMLButtonElement>(null);

  // Selección local: el hueco no se confirma (ni se crea el hold) hasta pulsar "Continuar".
  const [pickedTime, setPickedTime] = useState<string | null>(null);
  useEffect(() => {
    setPickedTime(null);
  }, [selectedDate, selectedResourceId]);

  // Scroll selected date to the left edge of the strip (without touching page scroll)
  useEffect(() => {
    const wrap = stripWrapRef.current;
    const btn = selectedBtnRef.current;
    if (!wrap || !btn) return;
    // 20px = left padding of date-strip-wrap
    wrap.scrollTo({ left: btn.offsetLeft - 20, behavior: 'smooth' });
  }, [selectedDate]);

  const summaryParts: string[] = [];
  if (service?.duration != null && service.duration !== '') summaryParts.push(formatDuration(service.duration));
  if (service?.price != null && service.price !== '') {
    const ps = formatPrice(service.price, service.currency);
    if (ps) summaryParts.push(ps);
  }
  const serviceSummary = summaryParts.join(' · ');

  const selNorm = selectedDate?.slice(0, 10) ?? null;

  // Mes visible: el de la fecha elegida o, si aún no hay, el del primer día del carrusel.
  const monthLabel = useMemo(() => {
    const ref = selNorm || dates[0]?.date;
    if (!ref) return '';
    try {
      const formatted = new Intl.DateTimeFormat(localeTag, { month: 'long', year: 'numeric' }).format(
        parseIsoDateLocal(ref),
      );
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    } catch {
      return '';
    }
  }, [selNorm, dates, localeTag]);

  // Mañana / tarde: corte a las 14:00, igual que el diseño de referencia.
  const { morning, afternoon } = useMemo(() => {
    const list = timeSlots ?? [];
    const morningList: TimeSlot[] = [];
    const afternoonList: TimeSlot[] = [];
    for (const ts of list) {
      const mins = minutesOfDay(ts.timeString);
      if (mins >= 0 && mins < 14 * 60) morningList.push(ts);
      else afternoonList.push(ts);
    }
    return { morning: morningList, afternoon: afternoonList };
  }, [timeSlots]);

  const picked = pickedTime ? (timeSlots ?? []).find((ts) => ts.timeString === pickedTime) ?? null : null;
  const pickedResourceName =
    selectedResourceId && resourceOptions
      ? resourceOptions.find((r) => r.id === selectedResourceId)?.name ?? null
      : null;
  const pickedDateLabel = selNorm
    ? (() => {
        try {
          return new Intl.DateTimeFormat(localeTag, { weekday: 'short', day: 'numeric', month: 'short' })
            .format(parseIsoDateLocal(selNorm))
            .replace(/\./g, '');
        } catch {
          return selNorm;
        }
      })()
    : '';

  const renderSlotGroup = (label: string, list: TimeSlot[]) => {
    if (list.length === 0) return null;
    return (
      <div className="bk-slot-group">
        <div className="bk-section-label">
          <span>{label}</span>
          <i aria-hidden />
        </div>
        <div className="bk-slot-grid">
          {list.map((ts) => {
            const isFull = ts.available === false || ts.totalCapacity <= 0;
            const cap = ts.totalCapacity;
            const pax = requestedPartySize;
            // Con aforo holgado el número no aporta nada: solo se muestra cuando escasea.
            const showCount = !isFull && cap > 0 && !(cap >= pax * 3 && cap >= 10);
            const isLow = showCount && cap <= pax + 2;
            const isPicked = pickedTime === ts.timeString;
            return (
              <button
                key={ts.timeString}
                type="button"
                disabled={isFull}
                aria-pressed={isPicked}
                className={[
                  'bk-slot',
                  isPicked ? 'bk-slot--picked' : '',
                  isFull ? 'bk-slot--full' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => !isFull && setPickedTime(ts.timeString)}
              >
                <span className="bk-slot-time">{ts.timeString}</span>
                <span className={`bk-slot-meta${isLow ? ' bk-slot-meta--low' : ''}`}>
                  {isFull
                    ? tl('booking.slot_full')
                    : showCount
                      ? isLow
                        ? tl('booking.slots_few_left', { count: cap })
                        : tl('booking.slots_available_short', { count: cap })
                      : ''}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const hasSlots = (timeSlots?.length ?? 0) > 0;

  return (
    <div className="booking-step booking-step--flow-left bk-step bk-step--slots">
      <BookingStepProgress
        current={2}
        stepLabel={tl('booking.step_of', { current: 2, total: 3 })}
        title={tl('booking.step2_title')}
      />

      {service?.name ? (
        <p className="bk-step-context">
          <strong>{service.name}</strong>
          {serviceSummary ? ` · ${serviceSummary}` : ''}
        </p>
      ) : null}

      {resourceOptions && resourceOptions.length > 1 && onResourceFilter ? (
        <div className="bk-resource-filter">
          <span className="bk-resource-filter-label">{tl('booking.professional')}</span>
          <div className="bk-chip-row" role="group" aria-label={tl('booking.professional')}>
            <button
              type="button"
              className={`bk-chip${!selectedResourceId ? ' bk-chip--on' : ''}`}
              aria-pressed={!selectedResourceId}
              onClick={() => onResourceFilter(null)}
            >
              {tl('booking.any_professional')}
            </button>
            {resourceOptions.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`bk-chip${selectedResourceId === r.id ? ' bk-chip--on' : ''}`}
                aria-pressed={selectedResourceId === r.id}
                onClick={() => onResourceFilter(r.id)}
              >
                {r.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="bk-date-block">
        <div className="bk-date-head">
          <span className="bk-date-month">{monthLabel}</span>
          <span className="bk-date-legend">
            <i aria-hidden />
            {tl('booking.has_openings')}
          </span>
        </div>

        <div ref={stripWrapRef} className={`date-strip-wrap${loadingSlots ? ' date-strip-wrap--loading' : ''}`}>
          <div className="date-strip" role="group" aria-label={tl('booking.select_date')}>
            {dates.map((dateOption) => {
              const iso = dateOption.date.slice(0, 10);
              const d = parseIsoDateLocal(iso);
              const weekday = weekdayShort(localeTag, d);
              const dayNum = d.getDate();
              const isUnavailable = dateOption.available === false;
              const isSelected = iso === selNorm;

              return (
                <button
                  key={iso}
                  ref={isSelected ? selectedBtnRef : undefined}
                  type="button"
                  aria-selected={isSelected}
                  aria-disabled={isUnavailable ? true : undefined}
                  disabled={isUnavailable || loadingSlots}
                  className={[
                    'date-strip-item',
                    'bk-day',
                    isSelected ? 'date-strip-item--selected' : '',
                    isUnavailable ? 'date-strip-item--unavailable' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => !isUnavailable && !loadingSlots && onDateSelect(iso)}
                >
                  <span className="date-strip-weekday">{weekday}</span>
                  <span className="date-strip-day">{dayNum}</span>
                  <span className="bk-day-dot" aria-hidden />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="slot-area bk-slot-area">
        {loadingSlots ? (
          <SlotSkeleton />
        ) : !selectedDate ? (
          noSlotsMessage ? (
            <p className="no-slots-message">{noSlotsMessage}</p>
          ) : (
            <p className="slot-area-empty">{tl('booking.select_date_hint')}</p>
          )
        ) : hasSlots ? (
          <>
            {renderSlotGroup(tl('booking.morning'), morning)}
            {renderSlotGroup(tl('booking.afternoon'), afternoon)}
            {resourceOptions && resourceOptions.length > 1 ? (
              <p className="bk-slot-hint">
                {selectedResourceId ? tl('booking.slot_hint_filtered') : tl('booking.slot_hint_any')}
              </p>
            ) : null}
          </>
        ) : (
          <p className="no-slots-message">{noSlotsMessage || tl('booking.no_slots_for_date')}</p>
        )}
      </div>

      {/* El resumen se mantiene montado mientras haya huecos: en escritorio es la columna derecha
          fija, y aparecer/desaparecer dejaría un hueco de 320px al elegir la hora. */}
      {hasSlots && !loadingSlots ? (
        <div className="bk-slot-footer" role="region" aria-label={tl('booking.step2_title')}>
          <div className="bk-slot-footer-info">
            <span className="bk-slot-footer-main">
              {picked ? `${pickedDateLabel} · ${picked.timeString}` : tl('booking.pick_a_time')}
            </span>
            <span className="bk-slot-footer-sub">
              {picked
                ? [
                    pickedResourceName ? tl('booking.with_professional', { name: pickedResourceName }) : null,
                    serviceSummary || null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : pickedDateLabel}
            </span>
          </div>
          <button
            type="button"
            className="btn-primary bk-slot-footer-cta"
            disabled={!picked}
            onClick={() => picked && onSlotSelect(picked.representativeSlotId, picked.timeString)}
          >
            {tl('booking.continue')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
